import isEqual from 'lodash/isEqual';
import tinyColor from 'tinycolor2';
import { useRef, useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { DatasetMutColumnType, FieldType, isColumnOfType, getSubstanceScoreValue, getRawScoringFactorByColumnId } from '@discngine/moosa-models';
import clamp from 'lodash/clamp';

const AVAILABLE_COLORS_FOR_SAR_COLORIZING = [
    [
        { r: 255, g: 77, b: 79 },
        { r: 255, g: 120, b: 117 },
        { r: 255, g: 163, b: 158 },
        { r: 255, g: 204, b: 199 },
        { r: 255, g: 241, b: 240 },
    ],
    [
        { r: 255, g: 169, b: 64 },
        { r: 255, g: 192, b: 105 },
        { r: 255, g: 213, b: 145 },
        { r: 255, g: 231, b: 186 },
        { r: 255, g: 247, b: 230 },
    ],
    [
        { r: 255, g: 236, b: 61 },
        { r: 255, g: 245, b: 102 },
        { r: 255, g: 251, b: 143 },
        { r: 255, g: 255, b: 184 },
        { r: 254, g: 255, b: 230 },
    ],
    [
        { r: 115, g: 209, b: 61 },
        { r: 149, g: 222, b: 100 },
        { r: 183, g: 235, b: 143 },
        { r: 217, g: 247, b: 190 },
        { r: 246, g: 255, b: 237 },
    ],
    [
        { r: 54, g: 207, b: 201 },
        { r: 92, g: 219, b: 211 },
        { r: 135, g: 232, b: 222 },
        { r: 181, g: 245, b: 236 },
        { r: 230, g: 255, b: 251 },
    ],
    [
        { r: 64, g: 169, b: 255 },
        { r: 105, g: 192, b: 255 },
        { r: 145, g: 213, b: 255 },
        { r: 186, g: 231, b: 255 },
        { r: 230, g: 247, b: 255 },
    ],
    [
        { r: 89, g: 126, b: 247 },
        { r: 133, g: 165, b: 255 },
        { r: 173, g: 198, b: 255 },
        { r: 214, g: 228, b: 255 },
        { r: 240, g: 245, b: 255 },
    ],
    [
        { r: 146, g: 84, b: 222 },
        { r: 179, g: 127, b: 235 },
        { r: 211, g: 173, b: 247 },
        { r: 239, g: 219, b: 255 },
        { r: 249, g: 240, b: 255 },
    ],
    [
        { r: 247, g: 89, b: 171 },
        { r: 255, g: 133, b: 192 },
        { r: 255, g: 173, b: 210 },
        { r: 255, g: 214, b: 231 },
        { r: 255, g: 240, b: 246 },
    ],
];
const SAR_IMAGE_SIZE_OPTIONS = {
    small: { label: 'Small', value: 'small', width: 258, height: 64 },
    medium: { label: 'Medium', value: 'medium', width: 300, height: 128 },
    large: { label: 'Large', value: 'large', width: 400, height: 180 },
    extraLarge: { label: 'Extra Large', value: 'extraLarge', width: 500, height: 250 },
};
const DEFAULT_COLORS = [
    '#e4744e',
    '#f2b06e',
    '#f6e652',
    '#98d268',
    '#489556',
    '#51a4aa',
    '#0093dc',
    '#5276bb',
    '#a376c2',
    '#92656e',
    '#ee7d87',
    '#b34454',
    '#bdba6f',
    '#e9f3a3',
    '#bed4a9',
    '#a0dbdd',
    '#c6f0fd',
    '#5041ad',
    '#bd9da4',
    '#828282',
];

// Converts an HSV color value to RGB.
// hue [0, 360] and sat and val are in [0, 1]
// Returns { r, g, b } in [0, 255]
function hsvToRgb(hue, sat, val) {
    const _h = (6 * hue) / 360, _i = Math.floor(_h), _f = _h - _i, _p = val * (1 - sat), _q = val * (1 - _f * sat), _t = val * (1 - (1 - _f) * sat), mod = _i % 6, _r = [val, _q, _p, _p, _t, val][mod], _g = [_t, val, val, _q, _p, _p][mod], _b = [_p, _p, _t, val, val, _q][mod];
    return { r: Math.round(_r * 255), g: Math.round(_g * 255), b: Math.round(_b * 255) };
}
function rgbToString(color) {
    return `rgb(${color.r},${color.g},${color.b})`;
}
// expect val is in  [0, 1]
function valueToRGB(val, palette) {
    const start = palette.startAngle;
    const end = palette.endAngle > palette.startAngle ? palette.endAngle : palette.endAngle + 360;
    const angle = start + val * (end - start);
    const simpleAngle = angle > 360 ? angle - 360 : angle;
    const color = hsvToRgb(simpleAngle, 1, 1);
    return rgbToString(color);
}
const mixTwoColors = (firstColor, secondColor, distance // distance of the first color from 0 to 1
) => {
    const w2 = distance;
    const w1 = 1 - w2;
    const rgb = {
        r: Math.round(firstColor.r * w1 + secondColor.r * w2),
        g: Math.round(firstColor.g * w1 + secondColor.g * w2),
        b: Math.round(firstColor.b * w1 + secondColor.b * w2),
    };
    return rgb;
};
const mixColors = (colors) => {
    const averagedColor = colors.reduce((previousColor, currentColor, index) => {
        const amount = 100 / (index + 1); // amount of the currentColor
        return tinyColor.mix(previousColor, currentColor, amount).toString('hex');
    });
    return averagedColor;
};
const comparePercentsFunc = (item, nextItem) => {
    if (item.percent > nextItem.percent) {
        return 1;
    }
    if (item.percent < nextItem.percent) {
        return -1;
    }
    return 0;
};
// expect val is in  [0, 1]
const getColorFromGradient = (value, gradient) => {
    const percentageValue = value * 100;
    const sortedGradient = [...gradient].sort(comparePercentsFunc);
    let colorRange = [];
    for (let i = 0; i <= sortedGradient.length; i++) {
        const gradientPercent = sortedGradient[i]?.percent ?? 0;
        if (percentageValue <= gradientPercent) {
            colorRange = [i, i];
            break;
        }
        if (percentageValue >= gradientPercent && !sortedGradient[i + 1]) {
            colorRange = [i, i];
            break;
        }
        if (percentageValue < sortedGradient[i + 1]?.percent) {
            colorRange = [i, i + 1];
            break;
        }
    }
    //Get the two closest colors
    const firstItem = sortedGradient[colorRange[0]];
    const secondItem = sortedGradient[colorRange[1]];
    const firstColor = firstItem?.color ?? '';
    const secondColor = secondItem?.color ?? '';
    const firstItemPercent = firstItem?.percent ?? 0;
    const secondItemPercent = secondItem?.percent ?? 0;
    //Calculate distance of the first color from 0 to 1
    const distance = colorRange[0] === colorRange[1]
        ? 1
        : (value - firstItemPercent / 100) /
            (secondItemPercent / 100 - firstItemPercent / 100);
    return mixTwoColors(firstColor, secondColor, distance);
};
const generateGradientString = (gradientType, angle, gradient) => {
    const sortedGradient = [...gradient].sort(comparePercentsFunc);
    const colors = sortedGradient.map((currentColor) => {
        return `rgb(${currentColor.color.r},${currentColor.color.g},${currentColor.color.b}) ${currentColor.percent}%`;
    });
    return `${gradientType}-gradient(${angle}deg, ${colors})`;
};
const isEqualColor = (color1, color2) => isEqual(color1, color2);
const rgbToHex = ({ r, g, b }) => {
    const rgb = (r << 16) | (g << 8) | (b << 0);
    return '#' + (0x1000000 + rgb).toString(16).slice(1);
};
const hexToRgb = (color) => {
    const bigint = parseInt(color.substring(1), 16);
    const redValue = (bigint >> 16) & 255;
    const greenValue = (bigint >> 8) & 255;
    const blueValue = bigint & 255;
    return { r: redValue, g: greenValue, b: blueValue };
};
function* getColor() {
    let i = 0;
    while (true) {
        yield DEFAULT_COLORS[i++];
        if (i === DEFAULT_COLORS.length) {
            i = 0;
        }
    }
}
const colorGenerator = getColor();

function fromNestedObject(css) {
    return Object.entries(css)
        .filter(([, include]) => include)
        .map(([clazz]) => clazz)
        .join(' ');
}
function classNames(...classes) {
    return classes
        .map((arg) => {
        switch (typeof arg) {
            case 'string':
                return arg;
            case 'object':
                return fromNestedObject(arg);
            default:
                return '';
        }
    })
        .filter((value) => value)
        .join(' ');
}

const decimalFormatter = new Intl.NumberFormat('en-En', {
    style: 'decimal',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
});

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

const useGetScrollbarWidth = () => {
    const didCompute = useRef(false);
    const widthRef = useRef(0);
    if (didCompute.current)
        return widthRef.current;
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    // TODO need to discuss
    //outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);
    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);
    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    // Removing temporary elements from the DOM
    outer.parentNode?.removeChild(outer);
    didCompute.current = true;
    widthRef.current = scrollbarWidth;
    return scrollbarWidth;
};

const useOutsideClick = (callback) => {
    const ref = useRef(null);
    useEffect(() => {
        const handleClick = ({ target }) => {
            if (ref.current && !ref.current.contains(target)) {
                callback();
            }
        };
        document.addEventListener('click', handleClick, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, [callback, ref]);
    return ref;
};

const useIsOverflow = (ref, deps = []) => {
    const [isOverflow, setIsOverflow] = useState(null);
    const checkOverflow = useCallback(() => {
        if (!ref.current)
            return;
        setIsOverflow(ref.current.scrollWidth > ref.current.clientWidth);
    }, [ref]);
    useLayoutEffect(() => {
        if (!ref.current)
            return;
        checkOverflow();
        const resizeObserver = new ResizeObserver(() => checkOverflow());
        resizeObserver.observe(ref.current);
        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, checkOverflow, ...deps]);
    return isOverflow;
};

const keyToColorIndex = (key) => {
    return parseInt(key.substr(0, 7) + key.substr(18), 16);
};

function humanFileSize(bytes) {
    const thresh = 1024;
    let size = bytes;
    const units = ['Kb', 'Mb', 'Gb', 'Tb'];
    let counter = -1;
    if (bytes < thresh)
        return bytes + ' byte';
    do {
        size /= thresh;
        ++counter;
    } while (size >= thresh && counter < units.length - 1);
    return `${size.toFixed(2)} ${units[counter]}`;
}
const formatter = new Intl.DateTimeFormat('en-GB');
function fullDateToGBFormat(date) {
    return formatter.format(date);
}
function convertToOldColumnMeta(metadata) {
    const typesMap = {
        [DatasetMutColumnType.CID]: FieldType.ID,
        [DatasetMutColumnType.arrayOfNumber]: null,
        [DatasetMutColumnType.boolean]: FieldType.String,
        [DatasetMutColumnType.datetime]: FieldType.String,
        [DatasetMutColumnType.molecule]: FieldType.Structure,
        [DatasetMutColumnType.number]: FieldType.Number,
        [DatasetMutColumnType.string]: FieldType.String,
        [DatasetMutColumnType.tags]: null,
        [DatasetMutColumnType.virtual]: null,
    };
    const type = typesMap[metadata.type];
    if (!type) {
        throw new Error(`Unsupported type ${metadata.type}`);
    }
    const statistics = {
        missingValues: metadata.missingValues ?? 0,
        min: 0,
        max: 0,
        histogramStep: 0,
        histogram: [],
        textCategories: null,
    };
    const oldMeta = {
        name: metadata.id,
        type,
        statistics,
        histogramMax: 0,
        histogramBars: metadata.histogram,
        dataWidth: 0,
        isDiscreteColumn: false,
    };
    if (isColumnOfType(metadata, DatasetMutColumnType.number)) {
        Object.assign(statistics, metadata.statistics);
        Object.assign(oldMeta, {
            histogramMax: Math.max(...(metadata.statistics?.histogram ?? [])),
            histogramBars: metadata.histogram,
            dataWidth: (metadata.statistics?.max ?? 0) - (metadata.statistics?.min ?? 0),
            isDiscreteColumn: metadata.isDiscrete,
        });
    }
    else if (isColumnOfType(metadata, DatasetMutColumnType.string)) {
        Object.assign(statistics, {
            textCategories: metadata.isDiscrete
                ? metadata.discreteStatistics.textCategories.map((category) => ({
                    value: category.value ?? '',
                    count: category.count,
                }))
                : null,
        });
        Object.assign(oldMeta, {
            histogramBars: metadata.histogram,
            isDiscreteColumn: metadata.isDiscrete,
        });
        oldMeta.statistics = statistics;
    }
    return oldMeta;
}

const isValidNumericString = (value) => {
    const reg = new RegExp('^[-]?[0-9]+$');
    return !isNaN(Number(value)) && reg.test(value);
};

const NO_COLOR = 'none';
const tagToColor = ({ value, scores = {}, columnIndexes = {}, rowId, tag, }) => {
    if (!tag.isColored) {
        return NO_COLOR;
    }
    // Scoring Column
    if (tag.isScoringColumn && rowId !== undefined) {
        const score = getSubstanceScoreValue(scores, rowId);
        return score == null
            ? NO_COLOR
            : rgbToString(getColorFromGradient(score, tag.gradient.scoredGradient));
    }
    // Scoring Colorizing Mode - works the same for numeric and discrete columns
    if (tag.isScoredColorizingMode && rowId !== undefined) {
        const factor = getRawScoringFactorByColumnId(scores, columnIndexes, rowId, tag.columnId);
        return factor == null
            ? NO_COLOR
            : rgbToString(getColorFromGradient(factor, tag.gradient.scoredGradient));
    }
    // Discrete Column, not Scoring Colorizing Mode
    if (tag.isDiscreteProperty) {
        const paletteValue = tag.discretePropertyValues.find((obj) => {
            switch (typeof value) {
                case 'number':
                    return value === Number(obj.value);
                case 'string':
                    return value === String(obj.value);
                default:
                    return false;
            }
        });
        const tagColor = paletteValue?.color;
        return tagColor ? rgbToString(tagColor) : NO_COLOR;
    }
    // The case of common numeric property
    const numericValue = Number(value);
    if (isNaN(numericValue)) {
        return NO_COLOR;
    }
    const divider = tag.range.max - tag.range.min || 1;
    const positionInGradient = clamp((numericValue - tag.range.min) / divider, 0, 1);
    return rgbToString(getColorFromGradient(positionInGradient, tag.gradient.datasetGradient));
};

const getHistogramRange = (metadata) => {
    const values = metadata.histogramBars.map((bar) => bar.x0);
    const min = Math.min(...values, metadata.statistics.min);
    const max = Math.max(...values, metadata.statistics.max);
    const delta = max === min ? 1 : (max - min) * 0.1;
    return {
        min: min - delta,
        max: max + delta,
    };
};

function generateAxe(maxValue, step) {
    const ticks = [];
    for (let i = step; i <= maxValue || i - maxValue < step; i += step) {
        ticks.push(i);
    }
    return ticks;
}
function calculateHistogramTicks(maxValue) {
    if (maxValue <= 5) {
        return generateAxe(maxValue, 1);
    }
    else if (maxValue <= 10) {
        return generateAxe(maxValue, 2);
    }
    else if (maxValue <= 25) {
        return generateAxe(maxValue, 5);
    }
    else if (maxValue <= 50) {
        return generateAxe(maxValue, 10);
    }
    else if (maxValue <= 100) {
        return generateAxe(maxValue, 20);
    }
    else if (maxValue <= 250) {
        return generateAxe(maxValue, 50);
    }
    else if (maxValue <= 500) {
        return generateAxe(maxValue, 100);
    }
    else if (maxValue <= 1000) {
        return generateAxe(maxValue, 200);
    }
    else if (maxValue <= 2500) {
        return generateAxe(maxValue, 500);
    }
    else if (maxValue <= 5000) {
        return generateAxe(maxValue, 1000);
    }
    else if (maxValue <= 30000) {
        return generateAxe(maxValue, 5000);
    }
    return generateAxe(maxValue, 10000);
}

let canvas = null;
const getTextWidth = (text) => {
    // re-use canvas object for better performance
    if (!canvas) {
        canvas = document.createElement('canvas');
    }
    const context = canvas.getContext('2d');
    return context?.measureText(text)?.width ?? 0;
};

const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay);
        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);
    return debouncedValue;
};

const extractErrorMessage = (error) => {
    if (typeof error === 'object' && error !== null && 'message' in error) {
        return error.message;
    }
    return 'Unknown error';
};

function assertUnreachable(x, msg = "Didn't expect to get here") {
    throw new Error(msg);
}

const isDefined = (value) => {
    return value !== null && value !== undefined;
};

/**
 * Performs topological sort on a directed graph.
 * If the graph is cyclic it will be detected and partial sorting returns
 *
 * @param graph
 * @param startNodeId - if node is defined the function returns topological sorting for a subgraph started from this node
 *
 * @return
 *  nodeIds - array of ids sorted topologically, for whole graph,
 *            in this case of subgraph it always starts with startNodeId
 *  isCyclic - always calculated for the whole graph
 *  nodeIdInCycle - if the graph is cyclic it returns one of nodeIds in the cycle
 */
function sortDirectedGraphTopologically(graph, startNodeId) {
    const graphClone = structuredClone(graph);
    //   L ← Empty list that will contain the sorted elements
    //   S ← Set of all nodes with no incoming edge
    //
    //   while S is not empty do
    //     remove a node n from S
    //     add n to L
    //     for each node m with an edge e from n to m do
    //       remove edge e from the graph
    //       if m has no other incoming edges then
    //          insert m into S
    //
    //   if graph has edges then
    //      return error   (graph has at least one cycle)
    //   else
    //     return L   (a topologically sorted order)
    const sorted = [];
    const subgraphSortingMode = startNodeId !== undefined;
    const sortedSubgraph = new Set();
    if (subgraphSortingMode)
        sortedSubgraph.add(startNodeId);
    const startingNodes = [...graphClone.values()]
        .filter((node) => node.in.size === 0)
        .map((node) => node.id);
    while (startingNodes.length) {
        const nodeId = startingNodes.shift();
        sorted.push(nodeId);
        const node = graphClone.get(nodeId);
        if (!node) {
            throw new Error(`Unexpectedly no node for ${nodeId}`);
        }
        if (sortedSubgraph.has(nodeId)) {
            for (const out of node.out) {
                sortedSubgraph.add(out);
            }
        }
        const outNodes = [...node.out.values()];
        for (const nextNodeId of outNodes) {
            const nextNode = graphClone.get(nextNodeId);
            // remove arrow
            node.out.delete(nextNodeId);
            nextNode.in.delete(nodeId);
            if (!nextNode.in.size) {
                startingNodes.push(nextNode.id);
            }
        }
    }
    const nodeInCycle = [...graphClone.values()].find((node) => !!node.in.size);
    const isCyclicGraph = !!nodeInCycle;
    return {
        nodeIds: subgraphSortingMode ? [...sortedSubgraph.values()] : sorted,
        isCyclicGraph,
        nodeIdInCycle: nodeInCycle?.id,
    };
}

export { AVAILABLE_COLORS_FOR_SAR_COLORIZING, DEFAULT_COLORS, SAR_IMAGE_SIZE_OPTIONS, assertUnreachable, calculateHistogramTicks, classNames, colorGenerator, convertToOldColumnMeta, decimalFormatter, extractErrorMessage, fullDateToGBFormat, generateGradientString, getColor, getColorFromGradient, getHistogramRange, getTextWidth, hexToRgb, hsvToRgb, humanFileSize, isDefined, isEqualColor, isValidNumericString, keyToColorIndex, mixColors, mixTwoColors, rgbToHex, rgbToString, sortDirectedGraphTopologically, tagToColor, useDebounce, useGetScrollbarWidth, useIsOverflow, useOutsideClick, usePrevious, valueToRGB };
//# sourceMappingURL=index.js.map
